import { Card, Empty, Spin, Table, Tag, Typography } from "antd";
import { Calendar, Package, Truck } from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { useGetUpcomingServiceTicketsByInventoryId } from "~/services/service-ticket";
import { capitalize, formatDate } from "~/utils";
import { JobStatus, ServiceType } from "~/utils/constants";

const { Text } = Typography;

interface UpcomingServiceTicketsProps {
  inventoryId: string;
}

const UpcomingServiceTickets: React.FC<UpcomingServiceTicketsProps> = ({
  inventoryId,
}) => {
  const navigate = useNavigate();
  const { data: serviceTicketsData, isLoading } =
    useGetUpcomingServiceTicketsByInventoryId(inventoryId);

  const handleRowClick = (record: any) => {
    const bookingId = record.jobTicket?.id;
    if (bookingId) {
      navigate(`/bookings/${bookingId}?tab=services&serviceId=${record.id}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Spin />
      </div>
    );
  }

  if (!serviceTicketsData || serviceTicketsData.items.length === 0) {
    return (
      <Empty
        description="No upcoming service tickets found for this item"
        className="py-8"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  const getServiceTypeIcon = (type: ServiceType) => {
    switch (type) {
      case ServiceType.DELIVERY:
        return <Truck size={14} />;
      case ServiceType.COLLECTION:
        return <Package size={14} />;
      default:
        return <Calendar size={14} />;
    }
  };

  const getStatusColor = (status: JobStatus) => {
    switch (status) {
      case JobStatus.DRAFT:
        return "orange";
      case JobStatus.SCHEDULED:
        return "blue";
      default:
        return "default";
    }
  };

  const columns = [
    {
      title: "Type",
      dataIndex: "serviceType",
      key: "serviceType",
      render: (text: ServiceType) => (
        <div className="flex items-center gap-1">
          {getServiceTypeIcon(text)}
          <span>{capitalize(text)}</span>
        </div>
      ),
    },
    {
      title: "Ticket",
      dataIndex: "serial",
      key: "serial",
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: "Client",
      dataIndex: "client",
      key: "client",
      render: (client: any) => client?.companyName || "N/A",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: JobStatus) => (
        <Tag color={getStatusColor(status)}>{capitalize(status)}</Tag>
      ),
    },
    {
      title: "Date",
      dataIndex: "serviceDatetime",
      key: "serviceDatetime",
      render: (date: string) => formatDate(date),
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      render: (location: any) => {
        if (!location) return "N/A";
        const city = location.city || "";
        const state = location.state || "";
        if (city && state) return `${city}, ${state}`;
        if (city) return city;
        if (state) return state;
        return "N/A";
      },
    },
  ];

  return (
    <Card bordered={false}>
      <Table
        dataSource={serviceTicketsData.items}
        columns={columns}
        rowKey="id"
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: { cursor: "pointer" },
        })}
        pagination={
          serviceTicketsData.items.length > 10
            ? {
                total: serviceTicketsData.meta.totalItems,
                current: serviceTicketsData.meta.currentPage,
                pageSize: serviceTicketsData.meta.itemsPerPage,
              }
            : false
        }
        size="small"
      />
    </Card>
  );
};

export default UpcomingServiceTickets;
